import { z } from 'zod'

import { ApiClientError } from '@ems/api-client/client'

import { api } from '#admin/lib/api-client.ts'

/**
 * The tenant manifest — what this edition is, and what it has bought.
 *
 * The schema lives with the feature that calls the endpoint, which is the guide's rule and
 * also what makes the response *parsed* rather than asserted. The SPA is a static file on
 * CloudFront, so being a few minutes out of step with the API after a release is the normal
 * state; a parse turns that into one legible error instead of a crash three components deep.
 */
export const ACCESS_LEVELS = ['none', 'read_only', 'full'] as const
export type AccessLevel = (typeof ACCESS_LEVELS)[number]

export const manifestSchema = z.object({
  tenant: z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    timezone: z.string(),
    defaultLocale: z.string(),
    currencyCode: z.string(),
    planKey: z.string(),
  }),
  modules: z.record(z.string(), z.enum(ACCESS_LEVELS)),
  branding: z.object({
    tokens: z.record(z.string(), z.string()),
    logoUrl: z.string().nullable(),
    faviconKey: z.string().nullable(),
    // Versioned (`?v=`), so a new upload is a new URL. `.default(null)`: the console is deployed
    // ahead of the API, and an older answer without it reads as "no favicon yet".
    faviconUrl: z.string().nullable().default(null),
  }),
})

export type Manifest = z.infer<typeof manifestSchema>

/**
 * `null` when the request resolves **no tenant**, which the endpoint answers with a 404.
 *
 * The manifest is ungated, so a 404 from it can only mean that. For platform staff on a fresh
 * stage it is the normal state rather than a failure: no tenant exists until they provision one
 * from `/platform`, and the console has to boot far enough to get them there. Throwing here
 * failed the authenticated layout, and with it every page -- including that one.
 */
export async function fetchManifest(signal?: AbortSignal): Promise<Manifest | null> {
  try {
    return await api.get('/v1/tenant/manifest', {
      schema: manifestSchema,
      ...(signal ? { signal } : {}),
    })
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 404 && error.code === 'not_found') {
      return null
    }
    throw error
  }
}

/**
 * Is a module usable, and how?
 *
 * Read from the manifest rather than inferred from whether a request 404'd. The guard's 404 is
 * deliberately indistinguishable from "no such route" — a tenant without `exhibitor` should
 * see an API with no exhibitor in it — so the manifest is the only honest source for deciding
 * which navigation to render.
 */
export function moduleAccess(manifest: Manifest | undefined, key: string): AccessLevel {
  return manifest?.modules[key] ?? 'none'
}
